/* Presentation only. Mirrored into the native Layout B patch. No product/cart requests. */
export const railEdges = (width, contentWidth, position) => {
  const maximum = Math.max(0, contentWidth - width);
  return { previous: maximum > 2 && position > 2, next: maximum > 2 && position < maximum - 2 };
};

export const shorterColumn = (heights, available, desktop) => {
  if (!desktop || heights.length !== 2 || heights.some((height) => height <= 0)) return -1;
  const index = heights[0] < heights[1] ? 0 : 1;
  return Math.abs(heights[0] - heights[1]) > 32 ? index : -1;
};

export const railControlMiddle = (card, frame, mobile, controlHeight = 44) => {
  const middle = card.offsetTop + card.offsetHeight / 2;
  const media = card.querySelector('.acqua-product-hub__image-link')?.getBoundingClientRect();
  if (!(media?.height > 0)) return middle;
  const imageTop = media.top - frame.getBoundingClientRect().top;
  // Preserve a safe wide-card midpoint only when the whole vertical target fits.
  const fitsImage = middle - controlHeight / 2 >= imageTop
    && middle + controlHeight / 2 <= imageTop + media.height;
  return mobile || !fitsImage ? imageTop + media.height / 2 : middle;
};

export const bindDragScroll = (rail) => {
  let pointerId = null;
  let startX = 0;
  let startY = 0;
  let startScrollLeft = 0;
  let dragged = false;
  let suppressClickUntil = 0;
  rail.addEventListener('pointerdown', (event) => {
    suppressClickUntil = 0;
    // Native touch scrolling retains momentum, vertical page scrolling and pinch zoom.
    if (event.pointerType === 'touch' || event.isPrimary === false || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    startScrollLeft = rail.scrollLeft;
    dragged = false;
  });
  rail.addEventListener('pointermove', (event) => {
    if (event.pointerId !== pointerId) return;
    const distance = event.clientX - startX;
    const verticalDistance = Math.abs(event.clientY - startY);
    if (!dragged && verticalDistance > 8 && verticalDistance >= Math.abs(distance)) {
      pointerId = null;
      return;
    }
    if (!dragged && Math.abs(distance) > 8 && Math.abs(distance) > verticalDistance) {
      dragged = true;
      rail.classList.add('is-dragging');
      rail.setPointerCapture?.(event.pointerId);
    }
    if (!dragged) return;
    event.preventDefault();
    rail.scrollLeft = startScrollLeft - distance;
  });
  const release = (event) => {
    if (event.pointerId !== pointerId) return;
    suppressClickUntil = dragged && event.type === 'pointerup' ? Date.now() + 300 : 0;
    rail.classList.remove('is-dragging');
    if (rail.hasPointerCapture?.(event.pointerId)) rail.releasePointerCapture(event.pointerId);
    pointerId = null;
    dragged = false;
  };
  rail.addEventListener('pointerup', release);
  rail.addEventListener('pointercancel', release);
  rail.addEventListener('pointerleave', (event) => { if (!dragged) release(event); });
  rail.addEventListener('lostpointercapture', () => {
    pointerId = null;
    dragged = false;
    rail.classList.remove('is-dragging');
  });
  rail.addEventListener('dragstart', (event) => event.preventDefault());
  rail.addEventListener('click', (event) => {
    const suppress = Date.now() < suppressClickUntil;
    suppressClickUntil = 0;
    if (!suppress || event.detail === 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);
};

const installPresentation = () => {
  if (document.querySelector('[data-acqua-product-interactions]')) return;
  const style = document.createElement('style');
  style.setAttribute('data-acqua-product-interactions', '');
  style.textContent = `
    .acqua-product-rail-frame { position: relative; min-width: 0; }
    .acqua-product-rail-frame > .acqua-product-rail { scrollbar-width: thin; scrollbar-color: #b6c5cc70 transparent; touch-action: auto; }
    .acqua-product-rail-frame > .acqua-product-rail:hover, .acqua-product-rail-frame > .acqua-product-rail:focus-within { scrollbar-color: #859da7 transparent; }
    .acqua-product-rail-frame > .acqua-product-rail::-webkit-scrollbar { height: 4px; }
    .acqua-product-rail-frame > .acqua-product-rail::-webkit-scrollbar-track { background: transparent; }
    .acqua-product-rail-frame > .acqua-product-rail::-webkit-scrollbar-thumb { background: #b6c5cc70; border-radius: 999px; }
    .acqua-product-rail-frame > .acqua-product-rail:hover::-webkit-scrollbar-thumb, .acqua-product-rail-frame > .acqua-product-rail:focus-within::-webkit-scrollbar-thumb { background: #859da7; }
    .acqua-product-rail.is-dragging { cursor: grabbing; scroll-behavior: auto; scroll-snap-type: none; user-select: none; }
    .acqua-product-rail-frame > .acqua-product-rail-controls {
      position: absolute; inset: 0 -12px; display: block; pointer-events: none; transform: none; z-index: 4;
    }
    .acqua-product-rail-frame > .acqua-product-rail-controls > button {
      position: absolute; top: var(--acqua-rail-middle, 50%); transform: translateY(-50%);
      width: 44px; height: 44px; min-width: 44px; border: 1px solid #b9cbd2; border-radius: 50%;
      background: #fff; color: #216080; font: inherit; font-size: 24px; line-height: 1;
      padding: 0 0 3px; pointer-events: auto; cursor: pointer; box-shadow: 0 2px 7px #06486212;
    }
    .acqua-product-rail-controls > button:first-child { left: 0; }
    .acqua-product-rail-controls > button:last-child { right: 0; }
    .acqua-product-rail-controls > button[hidden], .acqua-product-rail-controls[hidden] { display: none; }
    .acqua-product-rail-controls > button:hover { border-color: #216080; background: #f3f8fa; }
    .acqua-product-rail-controls > button:focus-visible, .acqua-product-rail:focus-visible { outline: 2px solid #216080; outline-offset: 2px; }
    .acqua-native-card-rail .flickity-prev-next-button { top: 50%; width: 44px; height: 44px; border-radius: 50%; }
    .acqua-native-card-rail .flickity-prev-next-button:disabled { visibility: hidden; pointer-events: none; }
    @media (max-width: 520px) {
      .acqua-product-hub-options .acqua-product-rail-frame > .acqua-product-rail-controls { inset-inline: 0; }
    }
    @media (min-width: 720px) {
      .acqua-product-sticky-pair { align-items: flex-start; }
      .acqua-product-sticky-pair > .acqua-product-sticky-column {
        position: sticky; top: var(--acqua-product-sticky-top); align-self: start;
      }
    }
    @media (prefers-reduced-motion: reduce) { .acqua-product-rail { scroll-behavior: auto; } }
  `;
  document.head.appendChild(style);
  const rails = new Map();
  const nativeRails = new Map();
  const pairs = new Map();
  let nextId = 0;
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; scan(); });
  };
  const enhanceNativeRail = (rail) => {
    if (nativeRails.has(rail)) return;
    const viewport = rail.querySelector('.flickity-viewport');
    const slider = rail.querySelector('.flickity-slider');
    // The theme's ProductRowScroller enables Flickity keyboard accessibility.
    // Do not create another scroller or attach another drag/keydown handler.
    if (!viewport || !slider || rail.getAttribute('tabindex') !== '0') return;
    rail.classList.add('acqua-native-card-rail', 'acqua-product-rail-frame');
    const controls = document.createElement('div');
    controls.className = 'acqua-product-rail-controls';
    rail.id ||= `acqua-native-product-rail-${++nextId}`;
    for (const [label, key, keyCode, icon] of [['Previous products', 'ArrowLeft', 37, '‹'], ['Next products', 'ArrowRight', 39, '›']]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-controls', rail.id);
      button.textContent = icon;
      button.addEventListener('click', () => {
        rail.focus({ preventScroll: true });
        const first = slider.querySelector('.productgrid--item');
        // Flickity contain/center may give several initial cells the same position.
        // A page-sized step makes the first arrow useful while retaining its native owner.
        const steps = Math.max(1, Math.floor(viewport.clientWidth / Math.max(first?.offsetWidth || 1, 1)) - 1);
        for (let step = 0; step < Math.min(steps, slider.children.length); step += 1) {
          rail.dispatchEvent(new KeyboardEvent('keydown', { key, code: key, keyCode, which: keyCode, bubbles: true }));
        }
      });
      controls.appendChild(button);
    }
    rail.appendChild(controls);
    const [previous, next] = controls.children;
    const update = () => {
      const cards = Array.from(slider.children).filter((card) => card.matches('.productgrid--item') && card.getBoundingClientRect().width > 0);
      const bounds = viewport.getBoundingClientRect();
      previous.hidden = previous.disabled = !cards.length || cards[0].getBoundingClientRect().left >= bounds.left - 2;
      next.hidden = next.disabled = !cards.length || cards.at(-1).getBoundingClientRect().right <= bounds.right + 2;
      controls.style.setProperty('--acqua-rail-middle', `${viewport.offsetTop + viewport.offsetHeight / 2}px`);
    };
    const movement = new MutationObserver(update);
    movement.observe(slider, { attributes: true, attributeFilter: ['style'], childList: true });
    const resize = new ResizeObserver(update);
    resize.observe(viewport);
    nativeRails.set(rail, { update, destroy: () => {
      movement.disconnect(); resize.disconnect(); controls.remove();
      rail.classList.remove('acqua-native-card-rail', 'acqua-product-rail-frame');
    } });
    update();
  };
  const enhanceRail = (rail) => {
    if (rails.has(rail)) return;
    const native = rail.matches('[data-product-row]');
    if (native && !rail.querySelector('.acqua-card-b')) return;
    // Native Flickity retains its own drag, swipe, keyboard and link event ownership.
    if (native && rail.classList.contains('flickity-enabled')) {
      enhanceNativeRail(rail);
      return;
    }
    // Theme-owned rows may initialize Flickity later: plain-scroll enhancement is opt-in.
    if (native && !rail.hasAttribute('data-acqua-native-scroll-rail')) return;
    if (native && !['auto', 'scroll'].includes(getComputedStyle(rail).overflowX)) return;
    let frame = rail.parentElement;
    if (!frame.matches('.acqua-product-hub-complete-room__carousel, .acqua-product-hub__carousel')) {
      frame = document.createElement('div');
      rail.before(frame);
      frame.appendChild(rail);
    }
    frame.classList.add('acqua-product-rail-frame');
    rail.classList.add('acqua-product-rail');
    rail.id ||= `acqua-product-rail-${++nextId}`;
    if (!rail.hasAttribute('tabindex')) rail.tabIndex = 0;
    if (!rail.hasAttribute('role')) rail.setAttribute('role', 'region');
    if (!rail.hasAttribute('aria-label') && !rail.hasAttribute('aria-labelledby')) {
      rail.setAttribute('aria-label', rail.closest('.acqua-product-hub-options__group')?.querySelector('h3, summary')?.textContent.trim() || 'Product suggestions');
    }
    let controls = frame.querySelector(':scope > .acqua-product-hub-complete-room__controls, :scope > .acqua-product-hub__carousel-controls');
    if (!controls) {
      controls = document.createElement('div');
      for (const [label, icon] of [['Previous products', '‹'], ['Next products', '›']]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', label);
        button.textContent = icon;
        controls.appendChild(button);
      }
      frame.appendChild(controls);
    }
    controls.classList.add('acqua-product-rail-controls');
    const [previous, next] = controls.querySelectorAll('button');
    previous.setAttribute('aria-controls', rail.id);
    next.setAttribute('aria-controls', rail.id);
    const update = () => {
      const edges = railEdges(rail.clientWidth, rail.scrollWidth, rail.scrollLeft);
      previous.hidden = previous.disabled = !edges.previous;
      next.hidden = next.disabled = !edges.next;
      if ((previous.hidden && document.activeElement === previous) || (next.hidden && document.activeElement === next)) rail.focus({ preventScroll: true });
      const first = Array.from(rail.children).find((child) => child.getBoundingClientRect().height > 0);
      const controlHeight = Math.max(44, previous.getBoundingClientRect().height, next.getBoundingClientRect().height);
      if (first) controls.style.setProperty('--acqua-rail-middle', `${railControlMiddle(first, frame, matchMedia('(max-width: 900px)').matches, controlHeight)}px`);
    };
    const scroll = (direction) => rail.scrollBy({
      left: direction * Math.max(rail.clientWidth * 0.85, 120),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
    });
    previous.addEventListener('click', () => scroll(-1));
    next.addEventListener('click', () => scroll(1));
    rail.addEventListener('keydown', (event) => {
      // Never steal keys from a select, option button or other nested control.
      if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') {
        rail.scrollTo({ left: event.key === 'Home' ? 0 : rail.scrollWidth, behavior: 'instant' });
      } else scroll(event.key === 'ArrowLeft' ? -1 : 1);
    });
    rail.addEventListener('scroll', update, { passive: true });
    rail.addEventListener('load', update, true);
    bindDragScroll(rail);
    const resize = new ResizeObserver(update);
    resize.observe(rail);
    Array.from(rail.children).forEach((child) => resize.observe(child));
    rails.set(rail, { update, resize });
    update();
  };
  const headerOffset = () => {
    let bottom = 0;
    document.querySelectorAll('.site-header, [data-site-header]').forEach((header) => {
      const position = getComputedStyle(header).position;
      const rect = header.getBoundingClientRect();
      if (['sticky', 'fixed'].includes(position) && rect.top <= 1 && rect.bottom > 0) bottom = Math.max(bottom, rect.bottom);
    });
    return bottom + 12;
  };
  const updatePair = (pair, columns) => {
    const top = headerOffset();
    const rectangles = columns.map((column) => column.getBoundingClientRect());
    const visible = columns.every((column) => column.isConnected && column.getBoundingClientRect().width > 0
      && column.getBoundingClientRect().height > 0);
    const sideBySide = rectangles[0].right <= rectangles[1].left + 1
      || rectangles[1].right <= rectangles[0].left + 1;
    const active = pair.matches('.acqua-product-hub-stage')
      ? !!pair.querySelector('.acqua-product-hub:not([hidden])')
      : !!pair.closest('.product__container--two-columns');
    pair.classList.toggle('acqua-product-sticky-pair', active && window.innerWidth >= 720);
    const index = shorterColumn(columns.map((column) => column.offsetHeight), window.innerHeight - top - 16,
      window.innerWidth >= 720 && sideBySide && visible && active);
    columns.forEach((column, candidate) => {
      column.classList.toggle('acqua-product-sticky-column', candidate === index);
      if (candidate === index) column.style.setProperty('--acqua-product-sticky-top',
        `${Math.min(top, window.innerHeight - column.offsetHeight - 16)}px`);
      else column.style.removeProperty('--acqua-product-sticky-top');
    });
  };
  const scan = () => {
    for (const [rail, state] of nativeRails) {
      if (!rail.isConnected || !rail.classList.contains('flickity-enabled')) { state.destroy(); nativeRails.delete(rail); }
      else state.update();
    }
    document.querySelectorAll('.acqua-product-hub__grid, .acqua-product-hub-complete-room__rail, .acqua-product-hub-options__values, [data-product-row]').forEach(enhanceRail);
    for (const [rail, state] of rails) {
      if (!rail.isConnected) { state.resize.disconnect(); rails.delete(rail); }
      else state.update();
    }
    document.querySelectorAll('.acqua-product-hub-stage, .product--outer').forEach((pair) => {
      if (pairs.has(pair)) return;
      const columns = pair.matches('.product--outer')
        ? Array.from(pair.children).filter((child) => child.matches('.product-gallery-wrapper, .product-gallery, .product-main'))
        : Array.from(pair.children);
      if (columns.length !== 2 || (pair.matches('.product--outer') && pair.closest('.product__container--three-columns'))) return;
      pair.classList.add('acqua-product-sticky-pair');
      const resize = new ResizeObserver(schedule);
      [pair, ...columns].forEach((node) => resize.observe(node));
      pairs.set(pair, { columns, resize });
    });
    for (const [pair, state] of pairs) {
      if (!pair.isConnected) {
        state.resize.disconnect();
        pair.classList.remove('acqua-product-sticky-pair');
        state.columns.forEach((column) => { column.classList.remove('acqua-product-sticky-column'); column.style.removeProperty('--acqua-product-sticky-top'); });
        pairs.delete(pair);
      } else updatePair(pair, state.columns);
    }
  };
  new MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList' || record.target.matches('details, .acqua-product-hub, .acqua-product-hub-options, [data-product-row]'))) schedule();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open', 'class'] });
  document.addEventListener('toggle', schedule, true);
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('scroll', schedule, { passive: true });
  scan();
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', installPresentation, { once: true });
  else installPresentation();
}
